import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    TemplateRef
} from '@angular/core';
import { EMPTY, race } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ApiDefaults, LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { Icons } from '../../../constants/icons.constants';
import { Messages } from '../../../constants/messages.constants';
import { Table } from '../../../constants/table.constants';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';

@LifecycleAware()
@Component({
    selector: 'lsq-data-table-mobile',
    template: `
        <div class="row">
            <div class="table-wrapper col">
                <div class="table-circle-loader" *ngIf="dataSource.loading$ | async">
                    <lsq-circle-loader></lsq-circle-loader>
                </div>

                <div
                    *ngIf="!(dataSource.loading$ | async) && (dataSource.noData$ | async)"
                    class="no-result-wrapper"
                >
                    <lsq-icon [type]="icons.NoDataReport"></lsq-icon>
                    <p class="text-text-gray">
                        {{ noDataMessage ? noDataMessage : defaultNoDataMessage }}
                    </p>
                </div>

                <div
                    *ngFor="
                        let row of dataSource.rows$ | async;
                        let index = index;
                        trackBy: trackByIndex
                    "
                    class="mobile-display-table"
                    [ngClass]="{ loading: dataSource.loading$ | async }"
                >
                    <lsq-data-table-mobile-card
                        [row]="row"
                        [isOpen]="index === 0 && initializeFirstOpen ? true : false"
                        [borderless]="borderlessCards"
                        [mobileCardHeader]="mobileCardHeader"
                        [mobileCardColumns]="mobileCardColumns"
                        [mobileCardFooter]="mobileCardFooter"
                        [cardsAreExpandable]="cardsAreExpandable"
                        [cardsAreClickable]="cardsAreClickable"
                        (rowExpanded)="rowExpanded.emit($event)"
                    >
                    </lsq-data-table-mobile-card>
                </div>

                <lsq-mobile-table-card-pagination
                    *ngIf="
                        isPaginated &&
                        !(dataSource.noData$ | async) &&
                        dataSource.take &&
                        (dataSource.totalCount$ | async) / dataSource.take > 1
                    "
                    [skip]="dataSource.skip ? dataSource.skip : 0"
                    [take]="dataSource.take ? dataSource.take : defaultTake"
                    [totalCount]="dataSource.totalCount$ | async"
                    [takeOptions]="takeOptions"
                    [isLoading]="dataSource.loading$ | async"
                    (skipChange)="handleSkipChange($event)"
                    (takeChange)="handleTakeChange($event)"
                >
                </lsq-mobile-table-card-pagination>
            </div>
        </div>
    `,
    styleUrls: ['./lsq-data-table-mobile.component.scss']
})
export class LsqDataTableMobileComponent implements AfterContentInit, OnDestroy {
    @ContentChild('mobileCardHeader') public mobileCardHeader: TemplateRef<any>;
    @ContentChild('mobileCardColumns') public mobileCardColumns: TemplateRef<any>;
    @ContentChild('mobileCardFooter') public mobileCardFooter: TemplateRef<any>;
    @Input() public dataSource: LsqListSource<any>;
    @Input() public isPaginated = false;
    @Input() public initializeFirstOpen = true;
    @Input() public borderlessCards = false;
    @Input() public takeOptions = Table.DefaultTakeOptions;
    @Input() public noDataMessage: string;
    @Input() public cardsAreExpandable = true;
    @Input() public cardsAreClickable = true;
    @Output() public rowExpanded = new EventEmitter<any>();
    @Output() public footerClick = new EventEmitter<any>();
    @Output() public skipChanged = new EventEmitter<number>();

    public columnNames: string[] = [];

    // Constants.
    public icons = Icons;
    public defaultNoDataMessage = Messages.NoResultsFound;
    public defaultTake = ApiDefaults.TakeDefault;

    constructor(private _changeDetectorRef: ChangeDetectorRef) {}

    public ngAfterContentInit(): void {
        // After init, and whenever data changes, run change detection.
        const data$ = this.dataSource.data$ || EMPTY;
        const rows$ = this.dataSource.rows$ || EMPTY;
        race(data$, rows$)
            .pipe(
                // The intention here is to update the view with the new values derived from
                // `dataSource.data$` and/or `dataSource.rows$`. A `delay` is required here in order
                // to make sure that the change detection runs after all the derived streams have
                // emitted.
                delay(0),
                forLifeOf(this)
            )
            .subscribe(() => {
                this._changeDetectorRef.detectChanges();
            });
    }

    public ngOnDestroy(): void {}

    public handleFooterClick(): void {
        this.footerClick.emit();
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public handleSkipChange(newSkip: number): void {
        this.dataSource.handleSkipChange(newSkip);
        this.skipChanged.emit(newSkip);
    }

    public handleTakeChange(newTake: number): void {
        this.dataSource.handleTakeChange(newTake);
    }
}
